import axios from "axios";
import {
  SPOT_VIEWER_BASIC_USERNAME,
  spotViewerAuthorizationHeader,
} from "../constants/spotViewerAuth";
import {
  buildFileDownloadUrlPath,
  fetchFileDownloadUrl,
} from "../files/filesService";
import { httpClient } from "../http/client";
import {
  executeExporterMultiple,
  exporterCsvToRecords,
  parseExporterCsv,
} from "./exporterService";

const SPOT_COLLEGES_EXPORTER_NAME = "SpotCollegesExporter";
const SPOT_COLLEGES_QUERY_TYPE = "All";
const SPOT_COLLEGES_PAGE_NUMBER = 1;
/** Fetch enough rows for hub cards + collegeId resolution. */
const SPOT_COLLEGES_PAGE_SIZE = 100;

/** Public hub college card model from SpotCollegesExporter. */
export interface SpotCollegeListItem {
  /**
   * Database college id for Viewer APIs (`college_id` / `collegeId`).
   * Never populated from `college_code` (DTE code) — that is not a Viewer id.
   */
  id: string | null;
  /** Optional DTE / display code when the exporter provides it separately. */
  collegeCode: string | null;
  name: string;
  location: string;
  /** Displayable college logo URL (direct exporter URL or resolved signed download URL). */
  logoUrl: string | null;
  /**
   * Stored logo file id when the exporter returns asset metadata instead of a URL.
   * Cleared after `fetchSpotColleges` resolves a signed download URL into `logoUrl`.
   */
  logoFileId?: number | null;
  /** Raw exporter round_status value. */
  roundStatus: string;
  /** True when the college currently has an active spot round. */
  hasActiveRound: boolean;
  openSeats: number;
}

/** Resolve the stored file id used for GET /api/v1/files/{fileId}/download-url. */
export function resolveSpotCollegeLogoFileId(
  college: Pick<SpotCollegeListItem, "logoFileId" | "logoUrl">,
): number | null {
  if (college.logoFileId != null && Number.isFinite(college.logoFileId)) {
    return college.logoFileId;
  }
  const rawLogoUrl = college.logoUrl?.trim();
  if (rawLogoUrl && /^\d+$/.test(rawLogoUrl)) {
    return Number(rawLogoUrl);
  }
  return null;
}

/**
 * Resolves a SpotCollegesExporter `logo_file_id` to a displayable signed URL.
 * GET /api/v1/files/{fileId}/download-url (Viewer Basic Auth).
 */
export async function fetchSpotCollegeLogoUrl(
  logoFileId: number,
): Promise<string | null> {
  if (!Number.isFinite(logoFileId)) {
    return null;
  }

  const authorization = spotViewerAuthorizationHeader();
  const downloadUrlPath = buildFileDownloadUrlPath(logoFileId);
  console.log("[PublicHub] logo download request", { logoFileId, downloadUrlPath });

  try {
    const downloadResponse = await fetchFileDownloadUrl(logoFileId, { authorization });
    const logoUrl = resolveAbsoluteMediaUrl(downloadResponse.url);
    console.log("[PublicHub] resolved logoUrl", { logoFileId, logoUrl });
    return logoUrl || null;
  } catch (error) {
    console.error("[PublicHub] logo download failed", { logoFileId, error });
    return null;
  }
}

/** Resolve one college's logo URL from exporter `logoFileId` metadata when needed. */
export async function resolveSpotCollegeItemLogo(
  college: SpotCollegeListItem,
): Promise<SpotCollegeListItem> {
  if (college.logoUrl?.trim()) {
    return college;
  }

  const logoFileId = resolveSpotCollegeLogoFileId(college);
  if (logoFileId == null) {
    return college;
  }

  const logoUrl = await fetchSpotCollegeLogoUrl(logoFileId);
  return {
    ...college,
    logoUrl,
    logoFileId,
  };
}

/**
 * Fetches signed download URLs for colleges that only have `logoFileId` metadata.
 * Colleges with a direct `logoUrl` from the exporter are returned unchanged.
 */
export async function enrichSpotCollegesWithLogos(
  colleges: SpotCollegeListItem[],
): Promise<SpotCollegeListItem[]> {
  return Promise.all(
    colleges.map(async (college) => {
      const logoFileId = resolveSpotCollegeLogoFileId(college);
      console.log("[PublicHub] logoFileId for college", {
        name: college.name,
        collegeId: college.id,
        logoFileId,
        existingLogoUrl: college.logoUrl,
      });

      if (college.logoUrl?.trim()) {
        return college;
      }

      if (logoFileId == null) {
        return college;
      }

      const logoUrl = await fetchSpotCollegeLogoUrl(logoFileId);
      return {
        ...college,
        logoUrl,
        logoFileId,
      };
    }),
  );
}

/**
 * Fetches Spot Colleges rows via the paginated exporter endpoint.
 * POST /admin/exporter/exportMultiple/SpotCollegesExporter/All/1/100 with body `{}`.
 *
 * Authenticated with Spot Viewer HTTP Basic (same credentials as Viewer APIs).
 */
export async function fetchSpotColleges(): Promise<SpotCollegeListItem[]> {
  const requestUrl =
    `/admin/exporter/exportMultiple/`
    + `${encodeURIComponent(SPOT_COLLEGES_EXPORTER_NAME)}/`
    + `${encodeURIComponent(SPOT_COLLEGES_QUERY_TYPE)}/`
    + `${SPOT_COLLEGES_PAGE_NUMBER}/${SPOT_COLLEGES_PAGE_SIZE}`;

  const authorization = spotViewerAuthorizationHeader();

  console.info("[SpotColleges] Request URL", requestUrl);
  console.info("[SpotColleges] Authorization mode", {
    mode: "Viewer Basic Auth",
    username: SPOT_VIEWER_BASIC_USERNAME,
    authorizationHeaderAttached: Boolean(authorization),
    authorizationScheme: authorization.startsWith("Basic ") ? "Basic" : "Other",
  });

  try {
    const exportResult = await executeExporterMultiple({
      exporter: SPOT_COLLEGES_EXPORTER_NAME,
      queryType: SPOT_COLLEGES_QUERY_TYPE,
      pageNumber: SPOT_COLLEGES_PAGE_NUMBER,
      pageSize: SPOT_COLLEGES_PAGE_SIZE,
      queryParams: {},
      authorization,
    });

    const rawPayload =
      exportResult.format === "json" ? exportResult.jsonData : exportResult.csvText;
    // TEMP DEBUG
    console.log("[Hallticket][SpotRound] raw exporter response", rawPayload);
    console.info("[SpotColleges] Raw exporter response", {
      requestUrl: exportResult.requestUrl,
      status: exportResult.status,
      contentType: exportResult.contentType,
      format: exportResult.format,
      payload: rawPayload,
    });

    let mapped: SpotCollegeListItem[];
    if (exportResult.format === "json") {
      mapped = mapSpotCollegesExporterPayload(exportResult.jsonData);
    } else {
      const table = parseExporterCsv(exportResult.csvText);
      mapped = mapSpotCollegesExporterPayload(exporterCsvToRecords(table));
    }

    // Public SpotRound returns mapped exporter rows as-is; logo resolution happens
    // per selected college (exporter logoFileId and/or viewer logo API).
    // TEMP DEBUG
    console.log("[SpotRound] mapped college object(s)", mapped);
    mapped.forEach((college, index) => {
      console.log("[SpotRound] mapped college", {
        index,
        collegeId: resolveSpotCollegeViewerId(college) ?? college.id ?? null,
        name: college.name,
        logoFileId: college.logoFileId ?? null,
        logoUrl: college.logoUrl ?? null,
      });
    });
    console.log("[SpotColleges] mapped college data (logos preserved)", mapped);
    console.info("[SpotColleges] Mapped college list (logos preserved)", mapped);
    return mapped;
  } catch (error) {
    console.error("[SpotColleges] API error", error);
    logSpotCollegesAuthFailureIfNeeded(error, authorization);
    throw error;
  }
}

/**
 * Maps flexible SpotCollegesExporter JSON/CSV payloads onto hub college cards.
 * Expected columns: college_name, location, logo_url / logoUrl, round_status, open_seats
 * (plus college_id when the exporter provides a Viewer database id).
 */
export function mapSpotCollegesExporterPayload(raw: unknown): SpotCollegeListItem[] {
  const items = extractExporterItems(raw);
  const mapped: SpotCollegeListItem[] = [];
  const seenKeys = new Set<string>();

  for (let index = 0; index < items.length; index += 1) {
    const item = normalizeSpotCollegeItem(items[index], index);
    if (!item) {
      continue;
    }
    const dedupeKey = item.id ?? `name:${item.name.toLowerCase()}`;
    if (seenKeys.has(dedupeKey)) {
      continue;
    }
    seenKeys.add(dedupeKey);
    mapped.push(item);
  }

  return mapped;
}

/**
 * Resolves the Viewer database college id used for board navigation + Viewer APIs.
 *
 * Preference order:
 * 1) SpotCollegesExporter `college_id` / `collegeId` when present
 * 2) Known Viewer id for colleges previously served on Public (exporter currently
 *    only defines college_name/location/round_status/open_seats — no college_id)
 *
 * Never uses DTE `college_code` (e.g. 6273).
 */
export function resolveSpotCollegeViewerId(
  college: Pick<SpotCollegeListItem, "id" | "name">,
): string | null {
  const fromExporter = college.id?.trim() || "";
  if (fromExporter) {
    return fromExporter;
  }

  const normalizedName = college.name.trim().toLowerCase();
  // Previous Public board was pinned to Viewer collegeId 246 (VIT Bibwewadi).
  if (normalizedName.includes("vishwakarma institute of technology")) {
    return "246";
  }

  return null;
}

/**
 * Finds an exporter row for a Viewer database college id.
 * Matches `college_id` from the exporter and known name fallbacks via
 * `resolveSpotCollegeViewerId`.
 */
export function findSpotCollegeByViewerId(
  colleges: SpotCollegeListItem[],
  viewerId: string,
): SpotCollegeListItem | undefined {
  const target = viewerId.trim();
  if (!target) {
    return undefined;
  }

  return colleges.find(
    (college) =>
      (college.id?.trim() || "") === target
      || resolveSpotCollegeViewerId(college) === target,
  );
}

/**
 * Picks the Viewer college to load: preferred id when present in the exporter
 * list, otherwise the first exporter row that resolves to a Viewer college id.
 */
export function pickDefaultSpotCollege(
  colleges: SpotCollegeListItem[],
  preferredId?: string | null,
): SpotCollegeListItem | null {
  const preferred = preferredId?.trim();
  if (preferred) {
    const directMatch = findSpotCollegeByViewerId(colleges, preferred);
    if (directMatch) {
      return directMatch;
    }
  }

  const withViewerId = colleges.filter(
    (college) => resolveSpotCollegeViewerId(college) != null,
  );
  if (preferred) {
    const match = withViewerId.find(
      (college) => resolveSpotCollegeViewerId(college) === preferred,
    );
    if (match) {
      return match;
    }
  }

  return withViewerId[0] ?? colleges[0] ?? null;
}

function logSpotCollegesAuthFailureIfNeeded(
  error: unknown,
  expectedAuthorization: string,
): void {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) {
    return;
  }

  const requestAuth = readRequestAuthorizationHeader(error.config?.headers);
  const expectedViewerAuth = spotViewerAuthorizationHeader();

  console.error("[SpotColleges] 401 Unauthorized diagnostics", {
    authorizationHeaderAttached: Boolean(requestAuth),
    authorizationScheme: getAuthorizationScheme(requestAuth),
    usingViewerBasicAuth: requestAuth === expectedViewerAuth,
    credentialsMatchViewerConstants: expectedAuthorization === expectedViewerAuth,
    expectedUsername: SPOT_VIEWER_BASIC_USERNAME,
    requestUrl: error.config?.url ?? null,
  });
}

function readRequestAuthorizationHeader(headers: unknown): string | null {
  if (!headers) {
    return null;
  }

  if (typeof (headers as { get?: (name: string) => unknown }).get === "function") {
    const value = (headers as { get: (name: string) => unknown }).get("Authorization");
    return typeof value === "string" && value.trim() ? value : null;
  }

  if (typeof headers !== "object") {
    return null;
  }

  const record = headers as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key.toLowerCase() === "authorization" && typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return null;
}

function getAuthorizationScheme(value: string | null): "Basic" | "Bearer" | "None" | "Other" {
  if (!value) {
    return "None";
  }
  if (value.startsWith("Basic ")) {
    return "Basic";
  }
  if (value.startsWith("Bearer ")) {
    return "Bearer";
  }
  return "Other";
}

function extractExporterItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    try {
      return extractExporterItems(JSON.parse(trimmed) as unknown);
    } catch {
      return [];
    }
  }

  if (!raw || typeof raw !== "object") {
    return [];
  }

  const root = raw as Record<string, unknown>;
  const directKeys = [
    "content",
    "items",
    "colleges",
    "data",
    "rows",
    "records",
    "list",
    "result",
    "exportData",
    "export_data",
  ] as const;

  for (const key of directKeys) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  for (const key of directKeys) {
    const value = root[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = extractExporterItems(value);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
}

function normalizeSpotCollegeItem(raw: unknown, _index: number): SpotCollegeListItem | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const record = flattenExporterRecord(raw as Record<string, unknown>);
  const name = firstString(record, [
    "college_name",
    "collegeName",
    "CollegeName",
    "name",
    "instituteName",
    "institute_name",
    "title",
    "college",
  ]);
  if (!name) {
    return null;
  }

  const location =
    firstString(record, [
      "location",
      "Location",
      "address",
      "Address",
      "city",
      "City",
      "college_location",
      "collegeLocation",
    ]) || "—";

  const roundStatus =
    firstString(record, [
      "round_status",
      "roundStatus",
      "RoundStatus",
      "status",
      "Status",
      "active_round_status",
      "activeRoundStatus",
    ]) || "";

  const openSeatsRaw = readFieldValue(record, [
    "open_seats",
    "openSeats",
    "OpenSeats",
    "total_available_seats",
    "totalAvailableSeats",
    "TotalAvailableSeats",
  ]);
  const parsedOpenSeats = toFiniteNumber(openSeatsRaw) ?? 0;

  const { logoUrl, logoFileId } = readLogoFromRecord(record);

  // Viewer database id only — never college_code / DTE code (e.g. 6273).
  const idRaw = firstString(record, [
    "college_id",
    "collegeId",
    "CollegeId",
    "College_Id",
    "COLLEGE_ID",
    "instituteId",
    "institute_id",
    "InstituteId",
    "id",
  ]);

  const collegeCode =
    firstString(record, [
      "college_code",
      "collegeCode",
      "CollegeCode",
      "College_Code",
      "code",
    ]) || null;

  // If `id` accidentally matched the DTE code column value, prefer a dedicated Viewer id.
  // Keep id only when it is not identical to college_code under a generic `id`/`code` collision.
  let viewerId = idRaw || null;
  if (viewerId && collegeCode && viewerId === collegeCode) {
    const dedicatedViewerId = firstString(record, [
      "college_id",
      "collegeId",
      "CollegeId",
      "College_Id",
      "COLLEGE_ID",
      "instituteId",
      "institute_id",
      "InstituteId",
    ]);
    viewerId = dedicatedViewerId || null;
  }

  if (!viewerId) {
    console.info("[SpotColleges] Row has no Viewer college_id (college_code is not used as id)", {
      index: _index,
      name,
      collegeCode,
      keys: Object.keys(record),
    });
  }

  return {
    id: viewerId,
    collegeCode,
    name,
    location,
    logoUrl,
    logoFileId,
    roundStatus,
    hasActiveRound: isActiveRoundStatus(roundStatus),
    openSeats: Math.max(0, Math.trunc(parsedOpenSeats)),
  };
}

/**
 * Maps exporter logo fields onto a display URL and/or stored file id.
 * Mirrors college-admin logo normalization: numeric values are file ids, not URLs.
 */
function readLogoFromRecord(
  record: Record<string, unknown>,
): { logoUrl: string | null; logoFileId: number | null } {
  const logoUrlRaw = firstString(record, [
    "logo_url",
    "logoUrl",
    "LogoUrl",
    "LOGO_URL",
    "college_logo_url",
    "collegeLogoUrl",
    "download_url",
    "downloadUrl",
    "signed_url",
    "signedUrl",
    "image_url",
    "imageUrl",
    "url",
  ]);

  const explicitFileId =
    toFiniteNumber(readFieldValue(record, [
      "logo_file_id",
      "logoFileId",
      "logo_file",
      "file_id",
      "fileId",
    ]))
    ?? toFiniteNumber(readFieldValue(record, [
      "college_logo_file_id",
      "collegeLogoFileId",
    ]));

  const genericLogoValue = readFieldValue(record, [
    "college_logo",
    "collegeLogo",
    "logo",
    "Logo",
  ]);
  const genericLogoString =
    typeof genericLogoValue === "string" && genericLogoValue.trim()
      ? genericLogoValue.trim()
      : "";
  const genericLogoNumber = toFiniteNumber(genericLogoValue);

  const numericLogoAsFileId =
    logoUrlRaw && /^\d+$/.test(logoUrlRaw)
      ? Number(logoUrlRaw)
      : genericLogoString && /^\d+$/.test(genericLogoString)
        ? Number(genericLogoString)
        : genericLogoNumber;

  const logoFileId = explicitFileId ?? numericLogoAsFileId ?? null;

  const resolvedDirectUrl =
    logoUrlRaw && !/^\d+$/.test(logoUrlRaw)
      ? resolveAbsoluteMediaUrl(logoUrlRaw)
      : genericLogoString
        && !/^\d+$/.test(genericLogoString)
        && (/^https?:\/\//i.test(genericLogoString) || genericLogoString.startsWith("/") || genericLogoString.startsWith("data:"))
        ? resolveAbsoluteMediaUrl(genericLogoString)
        : "";

  if (resolvedDirectUrl) {
    return {
      logoUrl: resolvedDirectUrl,
      logoFileId,
    };
  }

  return {
    logoUrl: null,
    logoFileId,
  };
}

/** Resolves relative media paths against the configured API base URL. */
function resolveAbsoluteMediaUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return "";
  }
  if (
    /^https?:\/\//i.test(trimmed)
    || trimmed.startsWith("data:")
    || trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }
  const baseURL = String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "");
  if (!baseURL) {
    return trimmed;
  }
  return `${baseURL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

/** Interprets SpotCollegesExporter round_status values (e.g. "ROUND ACTIVE"). */
export function isActiveRoundStatus(status: string): boolean {
  const normalized = status.trim().toLowerCase().replace(/[_-]+/g, " ");
  if (!normalized) {
    return false;
  }
  if (
    normalized.includes("no active")
    || normalized.includes("inactive")
    || normalized.includes("not active")
    || normalized.includes("ended")
    || normalized.includes("closed")
    || normalized.includes("completed")
  ) {
    return false;
  }
  return normalized.includes("active") || normalized === "live" || normalized === "open";
}

/**
 * Flattens one level of nested exporter objects (`college`, `institute`, etc.)
 * so `college.college_id` / `collegeId` fields are visible to field readers.
 */
function flattenExporterRecord(record: Record<string, unknown>): Record<string, unknown> {
  const nestedKeys = [
    "college",
    "College",
    "institute",
    "Institute",
    "details",
    "Details",
    "data",
    "row",
  ] as const;
  const flat: Record<string, unknown> = { ...record };
  for (const key of nestedKeys) {
    const nested = record[key];
    if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
      continue;
    }
    for (const [nestedKey, nestedValue] of Object.entries(nested as Record<string, unknown>)) {
      if (!(nestedKey in flat) || flat[nestedKey] == null || flat[nestedKey] === "") {
        flat[nestedKey] = nestedValue;
      }
    }
  }
  return flat;
}

function readFieldValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return record[key];
    }
  }

  const entries = Object.entries(record);
  for (const key of keys) {
    const target = normalizeFieldKey(key);
    const match = entries.find(([entryKey]) => normalizeFieldKey(entryKey) === target);
    if (match) {
      return match[1];
    }
  }

  return undefined;
}

function firstString(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }

  const entries = Object.entries(record);
  for (const key of keys) {
    const target = normalizeFieldKey(key);
    const match = entries.find(([entryKey]) => normalizeFieldKey(entryKey) === target);
    if (!match) {
      continue;
    }
    const value = match[1];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }

  return "";
}

/** Normalize exporter header variants: `College Id`, `college-id`, `COLLEGE_ID` → `collegeid`. */
function normalizeFieldKey(key: string): string {
  return key.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.trim().replace(/,/g, ""));
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}
